import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { hashPassword } from '@/lib/hashUtils';

interface PayPasswordModalProps {
  visible: boolean;
  totalPoints: number;
  loading: boolean;
  error: string;
  userId: string;
  onConfirm: (password: string) => void;
  onClose: () => void;
}

/* ── 内嵌 PinInput（隐藏 input + 可视格子，兼容移动端） ── */
const PinInput = ({ onComplete, color, autoFocus }: {
  onComplete: (v: string) => void;
  color: 'emerald' | 'rose';
  autoFocus?: boolean;
}) => {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue('');
    onComplete('');
    if (autoFocus) setTimeout(() => inputRef.current?.focus(), 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue(cleaned);
    onComplete(cleaned);
  };

  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? '');
  const activeIndex = Math.min(value.length, 5);
  const filled = color === 'emerald' ? 'border-emerald-400 bg-emerald-50' : 'border-rose-400 bg-rose-50';
  const active = color === 'emerald' ? 'border-emerald-400' : 'border-rose-400';
  const dot = color === 'emerald' ? 'bg-emerald-500' : 'bg-rose-500';
  const cursor = color === 'emerald' ? 'bg-emerald-400' : 'bg-rose-400';

  return (
    <div className="relative" onClick={() => inputRef.current?.focus()}>
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
        style={{ fontSize: 16 }}
      />
      <div className="flex items-center justify-between gap-2">
        {digits.map((digit, index) => {
          const isActive = focused && index === activeIndex;
          const isFilled = !!digit;
          return (
            <div
              key={index}
              className={`flex-1 h-[52px] flex items-center justify-center border-2 rounded-xl transition-all duration-150
                ${isActive ? `${active} bg-white` : isFilled ? filled : 'border-gray-200 bg-gray-50'}`}
            >
              {isFilled ? (
                <div className={`w-3 h-3 rounded-full ${dot}`} />
              ) : isActive ? (
                <div className={`w-0.5 h-6 ${cursor} animate-pulse`} />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PayPasswordModal = ({ visible, totalPoints, loading, error, userId, onConfirm, onClose }: PayPasswordModalProps) => {
  // mode: 'input' = 输入密码, 'setup' = 首次设置引导, 'creating' = 设置新密码
  const [mode, setMode] = useState<'input' | 'setup' | 'creating'>('input');
  const [checking, setChecking] = useState(true);
  const [hasPayPwd, setHasPayPwd] = useState(true);

  // 设置密码相关
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [setupErr, setSetupErr] = useState('');
  const [setupSaving, setSetupSaving] = useState(false);
  const [setupDone, setSetupDone] = useState(false);

  // 输入密码相关
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [shake, setShake] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // 打开时检查是否有支付密码
  useEffect(() => {
    if (!visible || !userId) return;
    setChecking(true);
    setMode('input');
    setSetupDone(false);
    setSetupErr('');
    setNewPwd('');
    setConfirmPwd('');
    setDigits(['', '', '', '', '', '']);
    setShake(false);

    supabase.from('custom_users').select('pay_password').eq('id', userId).maybeSingle()
      .then(({ data }) => {
        const has = !!data?.pay_password;
        setHasPayPwd(has);
        setMode(has ? 'input' : 'setup');
        setChecking(false);
        if (has) {
          setTimeout(() => inputRefs.current[0]?.focus(), 200);
        }
      });
  }, [visible, userId]);

  // 错误时抖动清空
  useEffect(() => {
    if (error) {
      setShake(true);
      setTimeout(() => {
        setShake(false);
        setDigits(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
      }, 500);
    }
  }, [error]);

  const handleChange = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = cleaned;
    setDigits(newDigits);
    if (cleaned && index < 5) inputRefs.current[index + 1]?.focus();
    if (cleaned && index === 5) {
      const pwd = newDigits.join('');
      if (pwd.length === 6) setTimeout(() => onConfirm(pwd), 100);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const nd = [...digits]; nd[index] = ''; setDigits(nd);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const nd = [...digits]; nd[index - 1] = ''; setDigits(nd);
      }
    }
  };

  const handleSubmit = () => {
    const pwd = digits.join('');
    if (pwd.length === 6) onConfirm(pwd);
  };

  const handleSetupSave = async () => {
    if (newPwd.length !== 6) { setSetupErr('请输入6位数字密码'); return; }
    if (!/^\d{6}$/.test(newPwd)) { setSetupErr('支付密码只能是数字'); return; }
    if (newPwd !== confirmPwd) { setSetupErr('两次密码不一致'); return; }
    setSetupSaving(true); setSetupErr('');
    const hash = await hashPassword(newPwd);
    const { error: dbErr } = await supabase.from('custom_users').update({ pay_password: hash }).eq('id', userId);
    setSetupSaving(false);
    if (dbErr) { setSetupErr('设置失败，请重试'); return; }
    setHasPayPwd(true);
    setSetupDone(true);
  };

  if (!visible) return null;
  const filled = digits.filter(Boolean).length;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={!loading && !setupSaving ? onClose : undefined} />
      <div className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white rounded-t-3xl z-50 px-6 pt-5 pb-10">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />

        {/* 加载中 */}
        {checking && (
          <div className="flex flex-col items-center py-10">
            <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm text-gray-400">验证中...</p>
          </div>
        )}

        {/* 输入支付密码 */}
        {!checking && mode === 'input' && (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-keyhole-line text-emerald-500 text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">输入支付密码</h3>
              <p className="text-sm text-gray-500">
                确认消耗 <span className="text-emerald-600 font-semibold">{totalPoints.toLocaleString()}</span> 积分完成兑换
              </p>
            </div>
            <div
              className="flex items-center justify-center gap-2.5 mb-2"
              style={shake ? { animation: 'shake 0.4s ease' } : {}}
            >
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`w-11 text-center text-xl font-bold border-2 rounded-xl outline-none transition-all duration-150
                    ${digit ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-900'}
                    ${error && !shake ? 'border-rose-300 bg-rose-50' : ''}
                    focus:border-emerald-400 focus:bg-white`}
                  style={{ height: '52px' }}
                  disabled={loading}
                />
              ))}
            </div>
            <div className="h-6 flex items-center justify-center mb-3">
              {error && !shake && (
                <div className="flex items-center gap-1.5">
                  <i className="ri-error-warning-line text-rose-500 text-sm" />
                  <p className="text-sm text-rose-500">{error}</p>
                </div>
              )}
            </div>
            <button
              onClick={handleSubmit}
              disabled={filled < 6 || loading}
              className={`w-full py-3.5 rounded-full font-medium text-sm cursor-pointer whitespace-nowrap transition-all
                ${filled === 6 && !loading ? 'bg-emerald-500 text-white active:scale-95' : 'bg-gray-100 text-gray-400'}`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  验证中...
                </span>
              ) : '确认支付'}
            </button>
            <button onClick={onClose} disabled={loading} className="w-full text-center text-sm text-gray-400 mt-3 cursor-pointer py-1 disabled:opacity-40">
              取消
            </button>
          </>
        )}

        {/* 首次设置引导 */}
        {!checking && mode === 'setup' && !setupDone && (
          <>
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-keyhole-line text-amber-500 text-3xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">设置支付密码</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                首次兑换需要设置支付密码<br />用于保护您的积分安全
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl px-4 py-3 mb-5">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-amber-500 text-sm mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 leading-relaxed">消耗 <span className="font-semibold">{totalPoints.toLocaleString()}</span> 积分，设置密码后将立即完成兑换</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">新支付密码（6位数字）</label>
                <PinInput onComplete={setNewPwd} color="emerald" autoFocus />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">确认支付密码</label>
                <PinInput onComplete={setConfirmPwd} color="emerald" />
              </div>
              {setupErr && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />{setupErr}
                </p>
              )}
              <button
                onClick={handleSetupSave}
                disabled={setupSaving || newPwd.length !== 6 || confirmPwd.length !== 6}
                className="w-full py-3.5 rounded-full font-medium text-sm cursor-pointer whitespace-nowrap bg-emerald-500 text-white disabled:opacity-50 disabled:bg-gray-200 disabled:text-gray-400"
              >
                {setupSaving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    设置中...
                  </span>
                ) : '设置并完成兑换'}
              </button>
              <button onClick={onClose} className="w-full text-center text-sm text-gray-400 cursor-pointer py-1">
                稍后再说
              </button>
            </div>
          </>
        )}

        {/* 设置成功 → 自动触发支付 */}
        {!checking && mode === 'setup' && setupDone && (
          <SetupSuccess
            totalPoints={totalPoints}
            newPwd={newPwd}
            onConfirm={onConfirm}
          />
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
        @keyframes popIn { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </>
  );
};

/* 设置成功后自动提交 */
const SetupSuccess = ({ totalPoints, newPwd, onConfirm }: { totalPoints: number; newPwd: string; onConfirm: (v: string) => void }) => {
  useEffect(() => {
    const t = setTimeout(() => onConfirm(newPwd), 1200);
    return () => clearTimeout(t);
  }, [newPwd, onConfirm]);

  return (
    <div className="flex flex-col items-center py-6">
      <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mb-4"
        style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
        <i className="ri-check-line text-emerald-500 text-3xl" />
      </div>
      <h3 className="font-bold text-gray-900 text-base mb-2">支付密码设置成功</h3>
      <p className="text-sm text-gray-500 text-center mb-4">
        正在为您完成 <span className="text-emerald-600 font-semibold">{totalPoints.toLocaleString()}</span> 积分兑换...
      </p>
      <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

export default PayPasswordModal;
